<script lang="ts">
	import type { PricingCalculation } from '../../../types';
	import { Calendar } from '@lucide/svelte';
	import { __, sprintf } from '@wordpress/i18n';

	type Props = {
		pricing: PricingCalculation;
		quantity: number;
		deliveryDate: string;
		completedSteps: number;
		totalSteps: number;
		isComplete: boolean;
		// Tax-related props
		showInclTax: boolean;
		taxRate: number;
		totalInclTax: number;
		subtotalInclTax: number;
		shippingInclTax: number;
		unitPriceInclTax: number;
		onToggleTax: (inclTax: boolean) => void;
	};

	let {
		pricing,
		quantity,
		deliveryDate,
		completedSteps,
		totalSteps,
		isComplete,
		showInclTax,
		taxRate,
		totalInclTax,
		subtotalInclTax,
		shippingInclTax,
		unitPriceInclTax,
		onToggleTax,
	}: Props = $props();

	// Format price to EUR
	function formatPrice(price: number): string {
		return new Intl.NumberFormat('nl-NL', {
			style: 'currency',
			currency: 'EUR',
			minimumFractionDigits: 2,
			maximumFractionDigits: 2
		}).format(price);
	}

	// Format unit price with more decimals for per-piece pricing
	function formatUnitPrice(price: number): string {
		return new Intl.NumberFormat('nl-NL', {
			style: 'currency',
			currency: 'EUR',
			minimumFractionDigits: 2,
			maximumFractionDigits: 4
		}).format(price);
	}

	// Round up to nearest 0.05 (matches cart/checkout rounding)
	function roundUpTo005(value: number): number {
		return Math.ceil(Math.round(value * 20 * 10000) / 10000) / 20;
	}

	// Calculate setup costs total
	const setupCostsTotal = $derived(pricing.setupCostPerUnit * quantity);
	const setupCostsTotalInclTax = $derived(setupCostsTotal * (1 + taxRate));

	// Calculate toeslag per order total
	const toeslagOrderTotal = $derived(pricing.effectiveToeslagOrder);
	const toeslagOrderTotalInclTax = $derived(toeslagOrderTotal * (1 + taxRate));

	// Get display values based on tax toggle
	const displayUnitPrice = $derived(showInclTax ? unitPriceInclTax : pricing.unitPrice);
	const displaySubtotal = $derived(showInclTax ? subtotalInclTax : pricing.subtotal);
	const displayShipping = $derived(showInclTax ? shippingInclTax : pricing.shipping);
	const displaySetupCosts = $derived(showInclTax ? setupCostsTotalInclTax : setupCostsTotal);
	const displayToeslagOrder = $derived(showInclTax ? toeslagOrderTotalInclTax : toeslagOrderTotal);
	const displayTotal = $derived(showInclTax ? totalInclTax : pricing.total);

	// Tax label suffix
	const taxLabel = $derived(showInclTax ? __('incl. BTW', 'wpc') : __('excl. BTW', 'wpc'));

	// Format delivery date to Dutch locale with day name
	const formattedDeliveryDate = $derived.by(() => {
		if (!deliveryDate) return '';
		// Parse dd-mm-yyyy format
		const [day, month, year] = deliveryDate.split('-').map(Number);
		const date = new Date(year, month - 1, day);

		return date.toLocaleDateString('nl-NL', {
			weekday: 'long',
			day: 'numeric',
			month: 'long',
			year: 'numeric'
		});
	});
</script>

<div class="wpc-pricing">
	<div class="wpc-pricing__header">
		<h2 class="wpc-pricing__title">{__('Totaaloverzicht', 'wpc')}</h2>
		{#if !isComplete}
			<span class="wpc-pricing__progress">
				{sprintf(__('%1$d/%2$d voltooid', 'wpc'), completedSteps, totalSteps)}
			</span>
		{/if}
	</div>

	<div class="wpc-pricing__content">
		{#if quantity > 0}
			<!-- Delivery Date Section -->
			{#if deliveryDate}
				<div class="wpc-delivery-section">
					<div class="wpc-delivery-section__icon">
						<Calendar size={20} />
					</div>
					<div class="wpc-delivery-section__content">
						<span class="wpc-delivery-section__label">{__('Verwachte verzenddatum', 'wpc')}</span>
						<span class="wpc-delivery-section__date">{formattedDeliveryDate}</span>
					</div>
				</div>
			{/if}

			<!-- Price Breakdown -->
			<div class="wpc-price-breakdown">
				<div class="wpc-price-row">
					<span class="wpc-price-row__label">
						{__('Prijs per stuk', 'wpc')}
						<span class="wpc-price-row__detail" data-wg-notranslate>{quantity.toLocaleString('nl-NL')} x {formatUnitPrice(displayUnitPrice)}</span>
					</span>
					<span class="wpc-price-row__value" data-wg-notranslate>{formatPrice(roundUpTo005(displayUnitPrice * quantity))}</span>
				</div>


				{#if toeslagOrderTotal > 0}
					<div class="wpc-price-row">
						<span class="wpc-price-row__label">{__('Toeslag per bestelling', 'wpc')}</span>
						<span class="wpc-price-row__value" data-wg-notranslate>{formatPrice(displayToeslagOrder)}</span>
					</div>
				{/if}

				{#if setupCostsTotal > 0}
					<div class="wpc-price-row">
						<span class="wpc-price-row__label">{__('Instelkosten', 'wpc')}</span>
						<span class="wpc-price-row__value" data-wg-notranslate>{formatPrice(displaySetupCosts)}</span>
					</div>
				{/if}

				{#if pricing.shipping > 0}
					<div class="wpc-price-row">
						<span class="wpc-price-row__label">{__('Verzendkosten', 'wpc')}</span>
						<span class="wpc-price-row__value" data-wg-notranslate>{formatPrice(displayShipping)}</span>
					</div>
				{:else if pricing.shippingOnRequest}
					<div class="wpc-price-row wpc-price-row--on-request">
						<span class="wpc-price-row__label">{__('Verzendkosten', 'wpc')}</span>
						<span class="wpc-price-row__value">{__('Op aanvraag', 'wpc')}</span>
					</div>
					<p class="wpc-text--muted" style="margin:4px 0 0;font-size:13px;line-height:1.5;">
						{__('De verzendkosten voor deze oplage naar uw land berekenen wij op aanvraag. Neem contact met ons op voor de juiste verzendkosten.', 'wpc')}
					</p>
				{/if}
			</div>

			<!-- Subtotal -->
			<div class="wpc-price-subtotal">
				<div class="wpc-price-row">
					<span class="wpc-price-row__label">{__('Subtotaal', 'wpc')}</span>
					<span class="wpc-price-row__value" data-wg-notranslate>{formatPrice(displaySubtotal)}</span>
				</div>
				<span class="wpc-price-tax-note">({taxLabel})</span>
			</div>

			<!-- Total -->
			<div class="wpc-price-total">
				<span class="wpc-price-total__label">{__('Totaal', 'wpc')}</span>
				<div class="wpc-price-total__amount">
					<span class="wpc-price-total__value" data-wg-notranslate>{formatPrice(displayTotal)}</span>
					<span class="wpc-price-total__tax">({taxLabel})</span>
				</div>
			</div>

			<!-- BTW Toggle -->
			<div class="wpc-btw-toggle">
				<button
					type="button"
					class="wpc-btw-toggle__btn"
					class:wpc-btw-toggle__btn--active={!showInclTax}
					onclick={() => onToggleTax(false)}
				>
					{__('Excl. BTW', 'wpc')}
				</button>
				<button
					type="button"
					class="wpc-btw-toggle__btn"
					class:wpc-btw-toggle__btn--active={showInclTax}
					onclick={() => onToggleTax(true)}
				>
					{__('Incl. BTW', 'wpc')}
				</button>
			</div>
		{:else}
			<p class="wpc-text--muted wpc-text--center">
				{__('Selecteer alle opties en een aantal om de prijs te zien', 'wpc')}
			</p>
		{/if}
	</div>
</div>
